import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type AppTheme = 'dark' | 'light';

const STORAGE_KEY = 'preferred-theme';
const DARK_CLASS = 'ion-palette-dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<AppTheme>('dark');
  currentTheme$ = this.currentThemeSubject.asObservable();

  constructor() {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    this.applyTheme(savedTheme === 'light' ? 'light' : 'dark');
  }

  get currentTheme(): AppTheme {
    return this.currentThemeSubject.value;
  }

  setTheme(theme: AppTheme) {
    localStorage.setItem(STORAGE_KEY, theme);
    this.applyTheme(theme);
  }

  toggleTheme() {
    this.setTheme(this.currentTheme === 'dark' ? 'light' : 'dark');
  }

  private applyTheme(theme: AppTheme) {
    document.documentElement.classList.toggle(DARK_CLASS, theme === 'dark');
    this.currentThemeSubject.next(theme);
    this.applyStatusBarStyle(theme);
  }

  private async applyStatusBarStyle(theme: AppTheme) {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({ color: theme === 'dark' ? '#000000' : '#ffffff' });
    } catch (error) {
      console.error('Error updating native status bar style', error);
    }
  }
}
